import { createEffect, createMemo } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { setCoworkAuthTokenProvider } from "@/cowork/api"
import {
  authApiBaseUrl,
  clearStoredAuth,
  resendVerification,
  storeAuth,
  type PasswordAuthResponse,
} from "@/cowork/auth-api"
import { passwordAuthError, passwordValidationError } from "@/cowork/auth-errors"

type CoworkAuthState = {
  initialized: boolean
  accountName: string
  accountEmail: string
  accessToken: string
  error: string
  fullName: string
  organization: string
  email: string
  password: string
  submitting: boolean
  verificationSentTo: string
  unverifiedEmail: string
}

export const { provider: CoworkAuthProvider, use: useCoworkAuth } = createSimpleContext({
  name: "CoworkAuth",
  gate: false,
  init: () => {
    const [state, setState] = createStore<CoworkAuthState>({
      initialized: false,
      accountName: storedValue("cowork.auth.name"),
      accountEmail: storedValue("cowork.auth.email"),
      accessToken: storedValue("cowork.auth.token"),
      error: "",
      fullName: "",
      organization: "",
      email: storedValue("cowork.auth.email"),
      password: "",
      submitting: false,
      verificationSentTo: "",
      unverifiedEmail: "",
    })

    createEffect(() => {
      setState("initialized", true)
    })

    const login = async (email = state.email, password = state.password) => authenticate("signin", email, password)
    const signup = async (
      email = state.email,
      password = state.password,
      fullName = state.fullName,
      organization = state.organization,
    ) => authenticate("signup", email, password, fullName, organization)

    const logout = async () => {
      clearStoredAuth()
      setState({ accessToken: "", accountName: "", accountEmail: "", password: "", error: "" })
    }

    const resend = async () => {
      const email = state.unverifiedEmail || state.verificationSentTo
      if (!email || state.submitting) return
      setState({ submitting: true, error: "" })
      try {
        await resendVerification(email)
        setState({ verificationSentTo: email, unverifiedEmail: "" })
      } catch (error) {
        setState("error", error instanceof Error ? error.message : "auth.request_failed")
      } finally {
        setState("submitting", false)
      }
    }

    const token = async () => state.accessToken
    setCoworkAuthTokenProvider(token)

    const configured = createMemo(() => true)
    const authenticated = createMemo(() => !!state.accessToken)

    return {
      state,
      configured,
      authenticated,
      initializing: () => !state.initialized,
      login,
      signup,
      logout,
      resend,
      token,
      setFullName: (fullName: string) => setState({ fullName, error: "" }),
      setOrganization: (organization: string) => setState({ organization, error: "" }),
      setError: (error: string) => setState("error", error),
      setEmail: (email: string) => setState({ email, error: "", unverifiedEmail: "" }),
      setPassword: (password: string) => setState({ password, error: "" }),
      dismissVerificationNotice: () => setState({ verificationSentTo: "", error: "" }),
    }

    async function authenticate(
      mode: "signin" | "signup",
      email: string,
      password: string,
      fullName?: string,
      organization?: string,
    ) {
      if (state.submitting) return
      const validationError = passwordValidationError(password)
      if (validationError) {
        setState("error", validationError)
        return
      }
      setState({ submitting: true, error: "", unverifiedEmail: "" })
      let navigating = false
      try {
        const response = await fetch(`${authApiBaseUrl()}/api/auth/${mode}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            ...(mode === "signup" ? { full_name: fullName, organization } : {}),
          }),
        }).catch(() => {
          throw new Error("auth.network_error")
        })
        if (!response.ok) {
          const key = await passwordAuthError(response, mode)
          if (key === "auth.email_not_verified") setState("unverifiedEmail", email)
          throw new Error(key)
        }
        if (mode === "signup") {
          setState({ verificationSentTo: email, password: "", error: "" })
          return
        }
        const body = (await response.json()) as PasswordAuthResponse
        storeAuth(body)
        // The next document restores auth; publishing it here starts runtime requests during unload.
        setState({ password: "", error: "" })
        window.location.assign("/")
        navigating = true
      } catch (error) {
        setState("error", error instanceof Error ? error.message : "auth.request_failed")
      } finally {
        if (!navigating) setState("submitting", false)
      }
    }
  },
})

function storedValue(key: string) {
  try {
    return localStorage.getItem(key) || ""
  } catch {
    return ""
  }
}
